import * as Popover from '@radix-ui/react-popover';

import style from './SupportWidget.module.scss';

import { SupportWidgetTestAttributes } from 'chaika-test-attributes';
import type { SupportTicketDetails } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { translate } from '@chaika/i18n/helper';

import { TicketStatusBadge } from './TicketStatusBadge';
import { formatTicketNumber } from './utils/ticketNumber';

export function TicketHeaderContent({ ticket }: { ticket?: SupportTicketDetails }) {
    const ticketNumber = formatTicketNumber(ticket?.number ?? null);

    return (
        <div
            className={style.ticketHeader}
            data-test={SupportWidgetTestAttributes.TicketHeader}
            data-testid={SupportWidgetTestAttributes.TicketHeader}
        >
            <span className={style.ticketTitleRow}>
                {ticketNumber && <span className={style.ticketNumber}>{ticketNumber}</span>}
                <strong
                    className={style.ticketTitle}
                    title={ticket && formatTitleTooltip(ticket.title, ticketNumber)}
                >
                    {ticket?.title ?? translate('supportLoading')}
                </strong>
            </span>
            {ticket && (
                <>
                    <TicketStatusBadge ticket={ticket} />
                    <span className={style.ticketMeta}>
                        {ticket.responsible
                            ? translate('supportResponsible', { $name: ticket.responsible.name })
                            : translate('supportResponsibleNotAssigned')}
                    </span>
                    {ticket.participants.length > 1 && <TicketParticipants ticket={ticket} />}
                </>
            )}
        </div>
    );
}

function formatTitleTooltip(title: string, ticketNumber: string | null): string {
    return ticketNumber === null ? title : `${ticketNumber} · ${title}`;
}

function TicketParticipants({ ticket }: { ticket: SupportTicketDetails }) {
    return (
        <Popover.Root>
            <Popover.Trigger asChild>
                <button
                    className={style.participantsButton}
                    type={'button'}
                >
                    {translate('supportParticipantsCount', { $count: String(ticket.participants.length) })}
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className={style.popover}
                    side={'bottom'}
                    align={'start'}
                    sideOffset={4}
                    aria-label={translate('supportParticipantsList')}
                >
                    <ul className={style.participantsList}>
                        {ticket.participants.map(participant => (
                            <li key={participant.id}>
                                {participant.id === store.authUser.id
                                    ? `${participant.name} ${translate('supportParticipantYou')}`
                                    : participant.name}
                            </li>
                        ))}
                    </ul>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
